import { __decorate } from "tslib";
import { html, LitElement } from 'lit';
import { property, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { watchContext } from '../../base/context';
import { ifNonEmpty } from '../../base/directives';
import { redispatchEvent } from '../../base/events';
import { ElementLogger } from '../../base/logger';
import { DEV_MODE } from '../../global/env';
import { scrubberPreviewContext } from '../scrubber-preview';
import { scrubberPreviewVideoElementStyles } from './styles';
export const SCRUBBER_PREVIEW_VIDEO_ELEMENT_TAG_NAME = 'vds-scrubber-preview-video';
/**
 * Used to load a low-resolution video to be displayed when the user is hovering or dragging
 * the scrubber thumb. The point at which they're hovering or dragging is the preview time position.
 * The video will automatically be updated to match, so ensure it's of the same length as the
 * original.
 *
 * 💡 The following attributes are updated for your styling needs:
 *
 * - `video-can-play`: Applied when the video is ready for playback.
 * - `video-error`: Applied when a media error has been encountered.
 *
 * 💡 The `canplay` and `error` events are re-dispatched by this element for you to listen to if
 * needed.
 *
 * @tagname vds-scrubber-preview-video
 * @csspart video
 * @example
 * ```html
 * <vds-scrubber>
 *   <vds-scrubber-preview>
 *     <vds-scrubber-preview-video
 *       src="/my-low-res-video.mp4"
 *     ></vds-scrubber-preview-video>
 *   </vds-scrubber-preview>
 * </vds-scrubber>
 * ```
 * @example
 * ```css
 * vds-scrubber-preview-video {
 *   bottom: 56px;
 * }
 *
 * vds-scrubber-preview-video::part(video) {
 *   max-width: 168px;
 * }
 * ```
 */
export class ScrubberPreviewVideoElement extends LitElement {
    constructor() {
        super(...arguments);
        // -------------------------------------------------------------------------------------------
        // Properties
        // -------------------------------------------------------------------------------------------
        /* c8 ignore next */
        this._logger = DEV_MODE && new ElementLogger(this);
        this._videoRef = createRef();
        // -------------------------------------------------------------------------------------------
        // Events
        // -------------------------------------------------------------------------------------------
        this._canPlay = false;
        this._hasError = false;
    }
    static get styles() {
        return [scrubberPreviewVideoElementStyles];
    }
    /**
     * The underlying `<video>` element.
     */
    get videoElement() {
        return this._videoRef.value;
    }
    // -------------------------------------------------------------------------------------------
    // Lifecycle
    // -------------------------------------------------------------------------------------------
    connectedCallback() {
        super.connectedCallback();
    }
    willUpdate(changedProperties) {
        if (changedProperties.has('src')) {
            this._canPlay = false;
            this._hasError = false;
            this.removeAttribute('video-can-play');
            this.removeAttribute('video-error');
        }
        super.willUpdate(changedProperties);
    }
    render() {
        return this._renderVideo();
    }
    _renderVideo() {
        return html `
      <video
        part="video"
        muted
        preload="metadata"
        src=${ifNonEmpty(this.src)}
        @canplay=${this._handleCanPlay}
        @error=${this._handleError}
        ${ref(this._videoRef)}
      ></video>
    `;
    }
    _handleCanPlay(event) {
        this._canPlay = true;
        this.setAttribute('video-can-play', '');
        /* c8 ignore start */
        if (DEV_MODE) {
            this._logger
                .debugGroup('preview video can play')
                .appendWithLabel('Video', this.videoElement)
                .appendWithLabel('Event', event)
                .end();
        }
        /* c8 ignore stop */
        redispatchEvent(this, event);
    }
    _handleError(event) {
        this._hasError = true;
        this.setAttribute('video-error', '');
        /* c8 ignore start */
        if (DEV_MODE) {
            this._logger
                .errorGroup('preview video error')
                .appendWithLabel('Video', this.videoElement)
                .appendWithLabel('Event', event)
                .end();
        }
        /* c8 ignore stop */
        redispatchEvent(this, event);
    }
    _handlePreviewTimeContextUpdate(previewTime) {
        if (!this._hasError &&
            this._canPlay &&
            this.videoElement.currentTime !== previewTime) {
            this.videoElement.currentTime = previewTime;
        }
    }
}
__decorate([
    property()
], ScrubberPreviewVideoElement.prototype, "src", void 0);
__decorate([
    state()
], ScrubberPreviewVideoElement.prototype, "_canPlay", void 0);
__decorate([
    state()
], ScrubberPreviewVideoElement.prototype, "_hasError", void 0);
__decorate([
    watchContext(scrubberPreviewContext.time)
], ScrubberPreviewVideoElement.prototype, "_handlePreviewTimeContextUpdate", null);
